import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Target, 
  Award, 
  TrendingUp, 
  Lightbulb, 
  Zap 
} from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 mix-blend-multiply" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            About Prestige AI
          </h1>
          <p className="mt-6 max-w-3xl text-xl text-cyan-100">
            Revolutionizing financial reporting and auditing with cutting-edge AI technology.
          </p>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8">
            <div>
              <h2 className="text-base font-semibold text-cyan-600 tracking-wide uppercase">Our Story</h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                From Vision to Reality
              </p>
              <p className="mt-4 text-lg text-gray-500">
                The auditing profession is built on precision, integrity, and regulatory compliance. However, the pressures of tight deadlines, frequent regulatory changes, and staffing shortages often make it difficult to uphold these standards without personal and professional sacrifices. After years in the industry, I realized that technology—if applied correctly—could bridge this gap.
              </p>
              <p className="mt-4 text-lg text-gray-500">
                Prestige AI was founded with a vision to provide small and medium-sized audit firms with the AI-powered tools needed to compete with larger firms, without the need for massive investments in infrastructure. Developed with insights from experienced professionals, our solutions address real challenges, streamlining financial statement reviews, compliance checks, and audit procedures—so auditors can focus on what truly matters.
              </p>
              <p className="mt-4 text-lg text-gray-500">
                Throughout this journey, I collaborated with a network of experienced professionals, industry colleagues, and trusted service providers who tested, refined, and contributed to the solutions. This collective effort ensures that our tools are both practical and effective, addressing real-world challenges faced by auditors and accountants worldwide.
              </p>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="pl-4 -mr-48 sm:pl-6 md:-mr-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
                <img
                  className="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:left-0 lg:h-full lg:w-auto lg:max-w-none"
                  src="/assets/aboutus1.avif"
                  alt="Team collaboration"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Founder Section */}
      <div className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div>
              <h2 className="text-base font-semibold text-cyan-600 tracking-wide uppercase">Our Founder</h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Gerhard De Beer
              </p>
              <p className="mt-3 text-lg text-gray-500">
                <span className="font-medium">Founder & CEO</span>
              </p>
              <p className="mt-3 text-lg text-gray-500">
                Gerhard De Beer is a South African Chartered Accountant (SA) and Registered Auditor with over 15 years of experience in the auditing profession. He spent his career at a large multinational audit firm, where he gained extensive experience in financial reporting, compliance, and audit quality across multiple industries. Rising through the ranks, he became an Audit Partner, signing off on listed clients and multinational corporations while also leading audit innovation initiatives within the firm.
              </p>
              <p className="mt-3 text-lg text-gray-500">
                Gerhard's deep expertise in auditing, combined with his passion for AI-driven efficiency, led him to establish Prestige AI. His vision is to equip small and medium-sized audit firms with the same cutting-edge AI capabilities that larger firms invest millions in developing, ensuring they remain competitive in an evolving industry. With a relentless focus on innovation, work-life balance, and precision, Gerhard continues to push the boundaries of how AI can redefine the future of auditing and financial reporting.
              </p>
            </div>
            <div className="mt-10 lg:mt-0">
              <div className="relative rounded-lg overflow-hidden shadow-xl bg-white p-6">
                <img
                  src="/assets/gdbphoto.png"
                  alt="Gerhard De Beer - Founder & CEO"
                  className="w-64 h-64 mx-auto object-contain rounded-lg"
                  style={{
                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission and Vision Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-cyan-600 font-semibold tracking-wide uppercase">Our Purpose</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Mission & Vision
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Mission */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="rounded-md bg-cyan-50 p-3 inline-block">
                    <Target className="h-6 w-6 text-cyan-600" />
                  </div>
                  <h3 className="mt-4 text-xl font-medium text-gray-900">Our Mission</h3>
                  <p className="mt-2 text-base text-gray-500">
                    To revolutionize auditing and financial reporting through AI-driven innovation, reducing manual workload, enhancing accuracy, and restoring work-life balance for finance professionals. By making cutting-edge AI technology affordable, accessible, and practical, we empower firms to stay competitive, improve efficiency, and deliver higher-quality audits and financial reviews.
                  </p>
                </div>
              </div>

              {/* Vision */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="rounded-md bg-cyan-50 p-3 inline-block">
                    <Lightbulb className="h-6 w-6 text-cyan-600" />
                  </div>
                  <h3 className="mt-4 text-xl font-medium text-gray-900">Our Vision</h3>
                  <p className="mt-2 text-base text-gray-500">
                    To be the trusted provider of AI-powered financial and audit solutions for small and medium-sized audit firms, equipping them with the technology needed to thrive in an evolving industry. Our goal is to remove barriers to AI adoption, enabling firms to enhance efficiency, compliance, and competitiveness without the heavy costs traditionally associated with advanced automation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-cyan-600 font-semibold tracking-wide uppercase">Our Values</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Drives Us
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our core values guide everything we do, from product development to customer service.
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {/* Value 1 */}
              <div className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-cyan-500 text-white">
                    <Award className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Excellence</h3>
                  <p className="mt-2 text-base text-gray-500">
                    We are committed to delivering the highest quality solutions that exceed expectations and set new standards in the industry.
                  </p>
                </div>
              </div>

              {/* Value 2 */}
              <div className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-cyan-500 text-white">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Innovation</h3>
                  <p className="mt-2 text-base text-gray-500">
                    We continuously push the boundaries of what's possible with AI in financial reporting, embracing new technologies and approaches.
                  </p>
                </div>
              </div>

              {/* Value 3 */}
              <div className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-cyan-500 text-white">
                    <Users className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Collaboration</h3>
                  <p className="mt-2 text-base text-gray-500">
                    We believe in the power of combining human expertise with AI capabilities, and we foster collaborative relationships with our clients.
                  </p>
                </div>
              </div>

              {/* Value 4 */}
              <div className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-cyan-500 text-white">
                    <Zap className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Integrity</h3>
                  <p className="mt-2 text-base text-gray-500">
                    We uphold the highest ethical standards in all our operations, ensuring transparency, security, and confidentiality.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-cyan-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to experience the future of financial reporting?</span>
            <span className="block text-cyan-200">Join Prestige AI today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-cyan-600 bg-white hover:bg-cyan-50"
              >
                Get started
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;